import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Search,
  Mail,
  Phone,
  Clock,
  Eye,
  Trash2,
  MailOpen,
  Download,
  MessageSquare,
  CheckCircle2,
  Circle,
  X,
  Loader2,
  Link2,
  Globe,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  get_all_contact,
  save_contact_admin,
  delete_website_master_data,
} from "../ServiceConnection/serviceconnection.js";

// ─── Constants ────────────────────────────────────────────────────────────────
// Backend Contact model:
// primary_id, name, email, phone, message, action, booking_status,
// from_where (0/null = contact form, 1 = estimate), source, source_url, flag
//
// Contact submissions = from_where=0 or null
// action field used as read/replied status: unread / read / replied
// source = where the lead came from (e.g. "contact_form", "blog_pop", "hero_cta" etc.)
// source_url = the page path the visitor was on when they submitted

const STATUS_CONFIG = {
  unread: {
    label: "Unread",
    color: "bg-primary/10 text-primary",
    icon: Circle,
  },
  read: {
    label: "Read",
    color: "bg-muted text-muted-foreground",
    icon: MailOpen,
  },
  replied: {
    label: "Replied",
    color: "bg-success/10 text-success",
    icon: CheckCircle2,
  },
};

const STATUS_OPTIONS = ["unread", "read", "replied"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

// Human-friendly label for a raw "source" value, e.g. "blog_pop" → "Blog Popup"
const formatSourceLabel = (source) => {
  if (!source) return "Website";
  return source
    .toString()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ─── Source badge + link (used in table + modal) ───────────────────────────────
const SourceCell = ({ source, sourceUrl }) => (
  <div className="space-y-1">
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
      <Globe className="h-2.5 w-2.5" />
      {formatSourceLabel(source)}
    </span>
    {sourceUrl && (
      <a
        href={sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={sourceUrl}
        className="flex max-w-[180px] items-center gap-1 truncate text-[11px] text-primary hover:underline"
      >
        <Link2 className="h-2.5 w-2.5 shrink-0" />
        <span className="truncate">{sourceUrl}</span>
      </a>
    )}
  </div>
);

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const DetailModal = ({ entry, onClose, onStatusChange, statusLoader }) => {
  if (!entry) return null;

  const entryStatus = entry.action || "unread";
  const cfg = STATUS_CONFIG[entryStatus] || STATUS_CONFIG["unread"];
  const StatusIcon = cfg.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-primary text-base font-bold text-white">
              {entry.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">
                {entry.name}
              </h2>
              <p className="text-xs text-muted-foreground">
                {formatDate(entry.entry_date)} at {formatTime(entry.entry_date)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 transition-colors hover:bg-accent"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-6">
          {/* Contact info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-muted/50 p-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Mail className="h-3 w-3" /> Email
              </p>
              <p className="break-all text-sm font-medium text-foreground">
                {entry.email || "—"}
              </p>
            </div>
            <div className="rounded-xl bg-muted/50 p-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Phone className="h-3 w-3" /> Phone
              </p>
              <p className="text-sm font-medium text-foreground">
                {entry.phone || "—"}
              </p>
            </div>
          </div>

          {/* Source info */}
          <div className="rounded-xl bg-muted/50 p-3">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Globe className="h-3 w-3" /> Source
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                {formatSourceLabel(entry.source)}
              </span>
              {entry.source_url && (
                <a
                  href={entry.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 break-all text-xs text-primary hover:underline"
                >
                  <Link2 className="h-3 w-3 shrink-0" />
                  {entry.source_url}
                </a>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="rounded-xl bg-muted/50 p-4">
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Message
            </p>
            <p className="text-sm leading-relaxed text-foreground">
              {entry.message || "—"}
            </p>
          </div>

          {/* Status update */}
          <div>
            <p className="mb-2 text-xs font-semibold text-foreground">
              Update Status
            </p>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => {
                const c = STATUS_CONFIG[s];
                const Icon = c.icon;
                const isActive = entryStatus === s;
                return (
                  <button
                    key={s}
                    disabled={statusLoader || isActive}
                    onClick={() => onStatusChange(entry.primary_id, s)}
                    className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-medium transition-all ${
                      isActive
                        ? "border-primary bg-primary text-white"
                        : "border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
                    }`}
                  >
                    {statusLoader && isActive ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Icon className="h-3 w-3" />
                    )}
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-border p-4">
          <a href={`mailto:${entry.email}`} className="flex-1">
            <Button className="w-full gap-2 rounded-xl" size="sm">
              <Mail className="h-3.5 w-3.5" /> Reply via Email
            </Button>
          </a>
          {entry.phone && (
            <a href={`tel:${entry.phone.replace(/\D/g, "")}`}>
              <Button variant="outline" size="sm" className="gap-2 rounded-xl">
                <Phone className="h-3.5 w-3.5" /> Call
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const ContactSubmissions = () => {
  const { toast } = useToast();

  const [entries, setEntries] = useState([]);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [statusLoader, setStatusLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(null); // primary_id being deleted
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");

  // ─── Fetch ──────────────────────────────────────────────────────────────
  // from_where=0/null → contact form submissions (not estimates)
  const fetchData = useCallback(async () => {
    try {
      setFetchLoader(true);

      const fd = new FormData();
      fd.append("flag", "1");

      const response = await server_post_data(get_all_contact, fd);

      if (!response.data.error) {
        // Exclude estimate requests (from_where=1) — keep only contact form
        const data = (response.data.message.data_contact || []).filter(
          (c) => String(c.from_where) !== "1",
        );
        setEntries(data);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setFetchLoader(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Status update ───────────────────────────────────────────────────────
  // action field = read/replied status
  // save_contact_admin with main_id + action
  const handleStatusChange = async (primary_id, newStatus) => {
    try {
      setStatusLoader(true);

      const fd = new FormData();
      fd.append("main_id", primary_id);
      fd.append("action", newStatus); // action field used as status

      const response = await server_post_data(save_contact_admin, fd);

      if (!response.data.error) {
        toast({
          title: "Updated",
          description: response.data.message,
        });

        // Update local state
        setEntries((prev) =>
          prev.map((e) =>
            e.primary_id === primary_id ? { ...e, action: newStatus } : e,
          ),
        );

        // Sync modal if open
        if (selected?.primary_id === primary_id) {
          setSelected((prev) => ({ ...prev, action: newStatus }));
        }
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setStatusLoader(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────
  // delete_website_master_data with flag_for=9 (Contact model)
  const handleDelete = async (primary_id) => {
    try {
      setDeleteLoader(primary_id);

      const fd = new FormData();
      fd.append("id_for_delete", primary_id);
      fd.append("flag_for", "9"); // Contact model in FLAG_MODEL_MAP
      fd.append("for_status_final", "d");

      const response = await server_post_data(delete_website_master_data, fd);

      if (!response.data.error) {
        toast({
          title: "Deleted",
          description: response.data.message,
        });

        setEntries((prev) => prev.filter((e) => e.primary_id !== primary_id));

        if (selected?.primary_id === primary_id) setSelected(null);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteLoader(null);
    }
  };

  // ─── Open entry — mark unread → read automatically ───────────────────────
  const handleOpen = (entry) => {
    setSelected(entry);
    const entryStatus = entry.action || "unread";
    if (entryStatus === "unread") {
      handleStatusChange(entry.primary_id, "read");
    }
  };

  // ─── Export CSV ──────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const headers = [
      "Name",
      "Email",
      "Phone",
      "Message",
      "Status",
      "Source",
      "Source URL",
      "Date",
    ];
    const rows = filtered.map((e) => [
      e.name || "",
      e.email || "",
      e.phone || "",
      `"${(e.message || "").replace(/"/g, "'")}"`,
      e.action || "unread",
      formatSourceLabel(e.source),
      e.source_url || "",
      formatDate(e.entry_date),
    ]);

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contact_submissions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Unique source options (built from whatever sources actually exist) ──
  const sourceOptions = useMemo(() => {
    const set = new Set();
    entries.forEach((e) => set.add(e.source || "website"));
    return Array.from(set).sort();
  }, [entries]);

  // ─── Filter + Search ─────────────────────────────────────────────────────
  const filtered = entries.filter((e) => {
    const matchSearch = [e.name, e.email, e.phone, e.message, e.source_url]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase());

    const entryStatus = e.action || "unread";
    const matchStatus = filterStatus === "all" || entryStatus === filterStatus;

    const entrySource = e.source || "website";
    const matchSource = filterSource === "all" || entrySource === filterSource;

    return matchSearch && matchStatus && matchSource;
  });

  // ─── Counts ──────────────────────────────────────────────────────────────
  const counts = STATUS_OPTIONS.reduce(
    (acc, s) => ({
      ...acc,
      [s]: entries.filter((e) => (e.action || "unread") === s).length,
    }),
    { all: entries.length },
  );

  // ─── UI ──────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              Contact Submissions
            </h2>
            <p className="text-sm text-muted-foreground">
              Customer messages received from your website contact form.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 self-start rounded-xl"
            onClick={handleExportCSV}
            disabled={filtered.length === 0}
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>

        {/* Status filter tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all", label: "All" },
            ...STATUS_OPTIONS.map((s) => ({
              key: s,
              label: STATUS_CONFIG[s].label,
            })),
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilterStatus(tab.key)}
              className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all ${
                filterStatus === tab.key
                  ? "bg-gradient-primary text-white shadow-glow"
                  : "border border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
              }`}
            >
              {tab.label}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  filterStatus === tab.key
                    ? "bg-white/20 text-white"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {counts[tab.key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search + Source filter */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, message, or URL..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="relative w-full sm:w-56">
            <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="flex h-9 w-full appearance-none rounded-lg border border-input bg-background pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="all">All Sources</option>
              {sourceOptions.map((src) => (
                <option key={src} value={src}>
                  {formatSourceLabel(src)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <Card>
          {/* Loading */}
          {fetchLoader && (
            <CardContent className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          )}

          {/* Empty */}
          {!fetchLoader && filtered.length === 0 && (
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
                <MessageSquare className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                No submissions found
              </p>
              <p className="text-xs text-muted-foreground">
                {search || filterStatus !== "all" || filterSource !== "all"
                  ? "Try adjusting your search or filters."
                  : "No contact messages received yet."}
              </p>
            </CardContent>
          )}

          {/* Rows */}
          {!fetchLoader && filtered.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Sender
                    </th>
                    <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground md:table-cell">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Message
                    </th>
                    <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground lg:table-cell">
                      Source
                    </th>
                    <th className="hidden px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:table-cell">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry) => {
                    const entryStatus = entry.action || "unread";
                    const cfg =
                      STATUS_CONFIG[entryStatus] || STATUS_CONFIG["unread"];
                    const StatusIcon = cfg.icon;
                    const isUnread = entryStatus === "unread";

                    return (
                      <tr
                        key={entry.primary_id}
                        className={`border-b border-border transition-colors hover:bg-muted/30 ${
                          isUnread ? "bg-primary/[0.02]" : ""
                        }`}
                      >
                        {/* Sender */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-white">
                              {entry.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="min-w-0">
                              <p
                                className={`text-sm text-foreground ${
                                  isUnread ? "font-bold" : "font-medium"
                                }`}
                              >
                                {entry.name}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {entry.email}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Phone */}
                        <td className="hidden px-6 py-4 md:table-cell">
                          <p className="text-sm text-muted-foreground">
                            {entry.phone || "—"}
                          </p>
                        </td>

                        {/* Message */}
                        <td className="max-w-[200px] px-6 py-4">
                          <p className="truncate text-sm text-muted-foreground">
                            {entry.message || "—"}
                          </p>
                        </td>

                        {/* Source */}
                        <td className="hidden px-6 py-4 lg:table-cell">
                          <SourceCell
                            source={entry.source}
                            sourceUrl={entry.source_url}
                          />
                        </td>

                        {/* Date */}
                        <td className="hidden px-6 py-4 sm:table-cell">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatDate(entry.entry_date)}
                          </div>
                        </td>

                        {/* Status badge */}
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${cfg.color}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title="View details"
                              onClick={() => handleOpen(entry)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <a href={`mailto:${entry.email}`}>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                title="Reply by email"
                              >
                                <Mail className="h-3.5 w-3.5" />
                              </Button>
                            </a>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              title="Delete"
                              disabled={deleteLoader === entry.primary_id}
                              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => handleDelete(entry.primary_id)}
                            >
                              {deleteLoader === entry.primary_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Footer count */}
        {!fetchLoader && filtered.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {entries.length} submissions
          </p>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <DetailModal
          entry={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
          statusLoader={statusLoader}
        />
      )}
    </>
  );
};

export default ContactSubmissions;